import clsx from 'clsx';
import { Element, Text } from 'html-react-parser';
import { useTranslations } from 'next-intl';

import type { ButtonVariant } from '@commons/components/Button';
import type { Template } from '@commons/components/RichText/templates';
import PageBuilder from '@commons/pagebuilder';
import WebformDrawer from '@commons/pagebuilder/webforms/Drawer';

import { textAlignments } from '..';

/**
 * FormButton CKEditor template.
 *
 * @returns A rendered button that opens a webform in a drawer.
 */

const FormButtonTemplate: Template = ({ nodes, attribs }) => {
  const t = useTranslations('commons');

  // Get the webform ID from the template attributes.
  const webformId = attribs['data-webform-id'];

  // Get the button label from the template first child.
  let label = t('learnMore');

  const anchorNode = nodes[0];
  if (anchorNode instanceof Element) {
    const textNode = anchorNode.children[0];
    if (textNode instanceof Text) label = textNode.data;
  }

  const classes: string[] = attribs.class?.split(' ') ?? [];
  const additionalClasses: string[] = [];

  classes.forEach((className) => {
    if (textAlignments.includes(className)) additionalClasses.push(className);
  });

  const anchorClasses =
    anchorNode instanceof Element ? (anchorNode.attribs.class.split(' ') ?? []) : [];

  const isButtonXL = anchorClasses.includes('xl');
  let buttonVariant: ButtonVariant = 'secondary';

  if (anchorClasses.includes('btn-primary')) buttonVariant = 'primary';
  if (anchorClasses.includes('btn-secondary')) buttonVariant = 'secondary';
  if (anchorClasses.includes('btn-tertiary')) buttonVariant = 'tertiary';

  const renderedWebform = PageBuilder.renderWebform(webformId, { inDrawer: true });

  return (
    <div className={clsx(additionalClasses)}>
      <WebformDrawer
        webformId={webformId}
        triggerProps={{ label, variant: buttonVariant, xl: isButtonXL }}
      >
        {renderedWebform}
      </WebformDrawer>
    </div>
  );
};

export default FormButtonTemplate;
